import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import api from '../../lib/api';

export type ScheduledNotificationType = 'WEEKLY_ORDER_SUMMARY';

export interface NotificationSchedule {
  type: ScheduledNotificationType;
  enabled: boolean;
  dayOfWeek: number; // 0=Sun .. 6=Sat
  hour: number; // 0..23
  minute: number;
  timezone: string;
  lastRunAt: string | null;
}

export interface UpdateNotificationSchedulePayload {
  enabled?: boolean;
  dayOfWeek?: number;
  hour?: number;
  minute?: number;
  timezone?: string;
}

export const notificationScheduleKeys = {
  all: ['notification-schedules'] as const,
};

export function useNotificationSchedules(enabled = true) {
  return useQuery({
    queryKey: notificationScheduleKeys.all,
    queryFn: async () => {
      const resp = await api.get<{ schedules: NotificationSchedule[] }>(
        '/organization/notifications/schedules'
      );
      return resp.data.schedules;
    },
    enabled,
  });
}

export function useUpdateNotificationSchedule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: {
      type: ScheduledNotificationType;
      payload: UpdateNotificationSchedulePayload;
    }) => {
      const resp = await api.put(
        `/organization/notifications/schedules/${args.type}`,
        args.payload
      );
      return resp.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: notificationScheduleKeys.all });
      void qc.invalidateQueries({ queryKey: ['auditLogs'] });
    },
  });
}

export interface NotificationRecipient {
  id: string;
  name: string | null;
  email: string;
  status: string;
  receivesWeeklyOrderSummary: boolean;
}

export const notificationRecipientKeys = {
  all: ['notification-recipients'] as const,
};

export function useNotificationRecipients(enabled = true) {
  return useQuery({
    queryKey: notificationRecipientKeys.all,
    queryFn: async () => {
      const resp = await api.get<{ recipients: NotificationRecipient[] }>(
        '/organization/notifications/recipients'
      );
      return resp.data.recipients;
    },
    enabled,
  });
}

export function useSetNotificationRecipient() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { customerId: string; receivesWeeklyOrderSummary: boolean }) => {
      const resp = await api.put(`/organization/notifications/recipients/${args.customerId}`, {
        receivesWeeklyOrderSummary: args.receivesWeeklyOrderSummary,
      });
      return resp.data;
    },
    // Optimistic: flip the checkbox immediately, roll back on error.
    onMutate: async (args) => {
      await qc.cancelQueries({ queryKey: notificationRecipientKeys.all });
      const prev = qc.getQueryData<NotificationRecipient[]>(notificationRecipientKeys.all);
      qc.setQueryData<NotificationRecipient[]>(notificationRecipientKeys.all, (old) =>
        (old ?? []).map((r) =>
          r.id === args.customerId
            ? { ...r, receivesWeeklyOrderSummary: args.receivesWeeklyOrderSummary }
            : r
        )
      );
      return { prev };
    },
    onError: (_err, _args, ctx) => {
      if (ctx?.prev) qc.setQueryData(notificationRecipientKeys.all, ctx.prev);
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: notificationRecipientKeys.all });
    },
  });
}
